/**
 * Candidate-solution contracts (the per-question "top 2-3 solutions")
 * ----------------------------------------------------------------------
 * Zod schemas for the ranked candidate solutions the bpr_candidate_solutions
 * skill proposes for ONE AI-captured scope question on a BPR or Continuous
 * Improvement deal (the build plan's "propose-solutions" ask: surface the top
 * 2-3 known ways to solve it, best fit first; a consultant picks one into the
 * question's Proposed solution). On engagement-less categories (CI) the batch
 * carries engagementUuid/frameworkVersionUuid null and no pattern grounding.
 *
 * Grounding discipline is part of the contract: every candidate cites ids
 * from the batch's groundingRefs manifest (exactly what the model was shown),
 * `patternKey` must be a REAL framework pattern key or null, account-specific
 * NetSuite identifiers are never invented - the literal VERIFY_IN_ACCOUNT
 * token stands in - and mutating steps live in `destructiveSteps` (sandbox
 * first). `needsReview` is computed server-side from the confidence floor
 * tunable, so the client renders policy without holding it.
 *
 * The batch persists on the question itself at
 * deal_adhoc_field.scope_meta.extra.solutionCandidates (the sanctioned
 * forward-compat bag - preserved by every brief write path), so candidates
 * and the picked marker survive reloads.
 */
import { z } from 'zod';
/** The literal placeholder the model must emit instead of any account-specific identifier. */
export const BPR_VERIFY_IN_ACCOUNT_TOKEN = 'VERIFY_IN_ACCOUNT';
/** The ScopeMeta.extra key the candidate batch lives under (deep-merged, never a whole-extra replace). */
export const SCOPE_EXTRA_SOLUTION_CANDIDATES_KEY = 'solutionCandidates';
/**
 * Deal categories where the ranked candidate-solutions flow is enabled.
 * Drives the UI gate (AdhocScopeControls), the generate route gate, and the
 * headless pipeline's SOLUTIONS stage - one allowlist, never per-layer checks.
 */
export const SOLUTION_CANDIDATE_CATEGORY_KEYS = ['bpr', 'continuous_improvement'];
/** True when the deal category participates in the candidate-solutions flow. */
export function solutionCandidatesEnabledForCategory(categoryKey) {
    return (categoryKey != null &&
        SOLUTION_CANDIDATE_CATEGORY_KEYS.includes(categoryKey));
}
export const BPR_SOLUTION_LEVELS = ['low', 'medium', 'high'];
export const bprSolutionLevelSchema = z.enum(BPR_SOLUTION_LEVELS);
export const BPR_SOLUTION_GROUNDING_KINDS = [
    'framework_pattern',
    'corpus_doc',
    'transcript',
    'similar_deal',
    'account_context',
];
export const bprSolutionGroundingKindSchema = z.enum(BPR_SOLUTION_GROUNDING_KINDS);
/** One entry of the grounding manifest - everything the model was shown, by stable id. */
export const bprSolutionGroundingRefSchema = z.object({
    /** Stable prompt-block id: 'pattern:<key>' | 'corpus:<n>' | 'transcript:<n>' | 'transcript:retrieval' | 'deal:<n>' | 'account'. */
    id: z.string().min(1),
    kind: bprSolutionGroundingKindSchema,
    /** Display title (source/document/pattern title). */
    title: z.string().optional(),
    /** knowledge.document uuid, when the ref is a document (corpus chunk / transcript). */
    documentUuid: z.string().optional(),
    /** knowledge.bpr_framework_source uuid, when the ref is an ingested source. */
    sourceUuid: z.string().optional(),
    /** Framework pattern key, when the ref is a framework pattern. */
    patternKey: z.string().optional(),
});
/** What the MODEL emits per candidate (server-computed fields excluded). */
export const bprSolutionCandidateModelSchema = z.object({
    /** Stable snake_case slug, unique within the batch. */
    id: z.string().min(1).max(120),
    /** 1 = best fit. */
    rank: z.number().int().min(1),
    title: z.string().min(1).max(200),
    /**
     * Customer-facing approach text - lands verbatim in the question's Proposed
     * solution on pick. Capped at 2000 to MATCH scopeMetaSchema's briefText cap
     * (types/scope.ts): a longer approach would silently truncate on Save.
     */
    approach: z.string().min(1).max(2000),
    pros: z.array(z.string().min(1)).max(6).default([]),
    cons: z.array(z.string().min(1)).max(6).default([]),
    risk: bprSolutionLevelSchema,
    effort: bprSolutionLevelSchema,
    /** What must be true/decided first; may embed the literal VERIFY_IN_ACCOUNT token. */
    prerequisites: z.array(z.string().min(1)).max(8).default([]),
    /** READ-ONLY checks in the customer account that validate fit. */
    verificationSteps: z.array(z.string().min(1)).max(8).default([]),
    /** Steps that mutate account data/config - must be sandbox-tested first. */
    destructiveSteps: z.array(z.string().min(1)).max(6).default([]),
    /** EXACT framework pattern key this instantiates; null when synthesized. */
    patternKey: z.string().nullable().default(null),
    /** Grounding-ref ids (batch.groundingRefs[].id) the claims rest on. */
    sources: z.array(z.string()).max(12).default([]),
    /** How well THIS client's evidence supports the fit (not generic pattern quality). */
    confidence: z.number().min(0).max(1),
    /** True only when the approach rests on cited provided context. */
    grounded: z.boolean(),
});
/** Persisted candidate = model shape + the server-computed review flag. */
export const bprSolutionCandidateSchema = bprSolutionCandidateModelSchema.extend({
    /** Computed at generation: !grounded OR confidence below the workspace floor. */
    needsReview: z.boolean().default(false),
});
/** The per-question batch persisted at scope_meta.extra.solutionCandidates. */
export const bprSolutionCandidateBatchSchema = z.object({
    version: z.literal(1).default(1),
    fieldKey: z.string().min(1),
    /** ISO timestamp of the generation run. */
    generatedAt: z.string(),
    engagementUuid: z.string().nullable().default(null),
    /** The framework version the pattern grounding came from (pinned or effective). */
    frameworkVersionUuid: z.string().nullable().default(null),
    modelRef: z.string().nullable().default(null),
    promptRef: z.string().nullable().default(null),
    groundingRefs: z.array(bprSolutionGroundingRefSchema).default([]),
    /** Ranked best-fit first. */
    candidates: z.array(bprSolutionCandidateSchema).min(1),
    /** The candidate the consultant chose ("Use this solution"); null until picked. */
    pickedCandidateId: z.string().nullable().default(null),
    pickedAt: z.string().nullable().default(null),
});
/** Safe parse for reads of scope_meta.extra.solutionCandidates. Never throws. */
export function parseBprSolutionCandidateBatch(input) {
    const result = bprSolutionCandidateBatchSchema.safeParse(input);
    return result.success ? result.data : null;
}
//# sourceMappingURL=bpr-solutions.js.map